import { mergeProps } from '@base-ui/react/merge-props';
import { useRender } from '@base-ui/react/use-render';
import * as React from 'react';

import { cn } from '@playbooks/ui/lib/utils';

type CardProps = useRender.ComponentProps<'div'> & { size?: 'default' | 'sm' };

function Card({ className, size = 'default', render, ...props }: CardProps) {
	// Theme
	const rootClassName = cn(
		'group/card flex flex-col gap-(--card-spacing) overflow-hidden rounded-media border bg-surface py-(--card-spacing) text-sm text-foreground transition-colors [--card-spacing:1rem] has-data-[slot=card-footer]:pb-0 has-[>img:first-child]:pt-0 data-[size=sm]:[--card-spacing:0.75rem] data-[size=sm]:has-data-[slot=card-footer]:pb-0 *:[img:first-child]:rounded-t-media *:[img:last-child]:rounded-b-media',
		className,
	);

	// Render
	return useRender({
		defaultTagName: 'div',
		props: mergeProps<'div'>({ className: rootClassName }, props),
		render,
		state: { slot: 'card', size },
	});
}

function CardHeader({ className, ...props }: React.ComponentProps<'div'>) {
	// Theme
	const rootClassName = cn(
		'group/card-header relative @container/card-header grid auto-rows-min items-start gap-1 rounded-t-media px-(--card-spacing) has-data-[slot=card-action]:grid-cols-[1fr_auto] has-data-[slot=card-description]:grid-rows-[auto_auto] [.border-b]:pb-(--card-spacing)',
		className,
	);

	// Render
	return <div data-slot='card-header' className={rootClassName} {...props} />;
}

function CardTitle({ className, ...props }: React.ComponentProps<'div'>) {
	// Theme
	const rootClassName = cn(
		'text-base leading-snug font-semibold group-data-[size=sm]/card:type-compact-title',
		className,
	);

	// Render
	return <div data-slot='card-title' className={rootClassName} {...props} />;
}

function CardDescription({ className, ...props }: React.ComponentProps<'div'>) {
	// Theme
	const rootClassName = cn(
		'text-sm leading-5 font-normal text-foreground-body group-data-[size=sm]/card:type-compact-body',
		className,
	);

	// Render
	return <div data-slot='card-description' className={rootClassName} {...props} />;
}

function CardAction({ className, ...props }: React.ComponentProps<'div'>) {
	// Theme
	const rootClassName = cn('col-start-2 row-span-2 row-start-1 self-start justify-self-end', className);

	// Render
	return <div data-slot='card-action' className={rootClassName} {...props} />;
}

function CardMedia({ className, ...props }: React.ComponentProps<'div'>) {
	// Theme
	const rootClassName = cn('flex shrink-0 items-center justify-center', className);

	// Render
	return <div data-slot='card-media' className={rootClassName} {...props} />;
}

function CardContent({ className, ...props }: React.ComponentProps<'div'>) {
	// Theme
	const rootClassName = cn('px-(--card-spacing)', className);

	// Render
	return <div data-slot='card-content' className={rootClassName} {...props} />;
}

function CardFooter({ className, ...props }: React.ComponentProps<'div'>) {
	// Theme
	const rootClassName = cn('flex items-center rounded-b-media border-t bg-surface p-(--card-spacing)', className);

	// Render
	return <div data-slot='card-footer' className={rootClassName} {...props} />;
}

export { Card, CardHeader, CardFooter, CardTitle, CardAction, CardDescription, CardContent, CardMedia };
export type { CardProps };
